import { useState, useEffect, useCallback, useRef } from 'react'
import { storageService } from '../services/storage'
import type { FileUpload, ToastMessage } from '../types/app'

export const useFileUpload = () => {
  const [uploads, setUploads] = useState<Record<string, FileUpload>>({})
  const [loading, setLoading] = useState(false)

  const uploadFile = async (
    file: File, 
    type: 'image' | 'video', 
    onProgress?: (progress: number) => void
  ) => {
    const uploadId = crypto.randomUUID()
    
    try {
      storageService.validateFile(file, type)
      
      const preview = await createFilePreview(file)
      
      setUploads(prev => ({
        ...prev,
        [uploadId]: {
          id: uploadId,
          file,
          preview,
          progress: 0,
          status: 'uploading',
          uploadedAt: new Date()
        }
      }))

      setLoading(true)

      const key = `${type}s/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
      
      const fileKey = await storageService.upload(key, file, (progress) => {
        setUploads(prev => ({
          ...prev,
          [uploadId]: {
            ...prev[uploadId],
            progress
          }
        }))
        onProgress?.(progress)
      })

      const url = await storageService.getUrl(fileKey)

      setUploads(prev => ({
        ...prev,
        [uploadId]: {
          ...prev[uploadId],
          status: 'completed',
          url,
          progress: 100
        }
      }))

      setLoading(false)
      return { uploadId, url, key: fileKey }
    } catch (error) {
      setUploads(prev => ({
        ...prev,
        [uploadId]: {
          ...prev[uploadId],
          status: 'error',
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed'
        }
      }))
      setLoading(false)
      throw error
    }
  }

  const removeUpload = (uploadId: string) => {
    setUploads(prev => {
      const newUploads = { ...prev }
      delete newUploads[uploadId]
      return newUploads
    })
  }

  const clearUploads = () => {
    setUploads({})
  }

  return {
    uploads,
    loading,
    uploadFile,
    removeUpload,
    clearUploads
  }
}

const createFilePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export const useToast = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const addToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = crypto.randomUUID()
    const newToast: ToastMessage = {
      ...toast,
      id,
      duration: toast.duration || 5000
    }

    setToasts(prev => [...prev, newToast])

    if ((newToast.duration ?? 5000) > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration ?? 5000)
    }

    return id
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const clearToasts = useCallback(() => {
    setToasts([])
  }, [])

  const showSuccess = useCallback((title: string, message?: string) => {
    return addToast({ type: 'success', title, message })
  }, [addToast])

  const showError = useCallback((title: string, message?: string) => {
    return addToast({ type: 'error', title, message })
  }, [addToast])

  const showWarning = useCallback((title: string, message?: string) => {
    return addToast({ type: 'warning', title, message })
  }, [addToast])

  const showInfo = useCallback((title: string, message?: string) => {
    return addToast({ type: 'info', title, message })
  }, [addToast])

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }
}

export const useDebounce = <T>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export const useInterval = (callback: () => void, delay: number | null) => {
  const savedCallback = useRef<() => void>()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    const tick = () => {
      savedCallback.current?.()
    }

    if (delay !== null) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export const useClickOutside = (handler: () => void) => {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handler])

  return ref
}

export const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    
    const updateMatch = () => {
      setMatches(media.matches)
    }

    updateMatch()
    media.addEventListener('change', updateMatch)
    
    return () => {
      media.removeEventListener('change', updateMatch)
    }
  }, [query])

  return matches
}